import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from 'uuid';
import { useNavigate, useParams } from "react-router-dom";
import api from '../api/dashboard/DashboardDashboardReq';
import handleDownloadCsv from '../utils/downloadCsv';
import LineChart from '../components/Chart';
import { toast } from 'sonner';
import { format, parseISO, isBefore, differenceInDays, differenceInHours } from 'date-fns';
import Spinner from "../components/Spinner";
import createSocket from '../api/BaseReqWs';
import json_parser from "../utils/json_parser";
import DashboardMainTable from "../components/DashboardMainTable";
import DashboardRange from "../components/DashboardRange";
import getChartYRange from "../utils/getChartYRange";

const chartSelections = (name, additional) => [
    { id: 0, selected: name },
    ...additional.filter(({ selected }) => selected && selected !== 'Choose'),
];

const chartSelectionKey = (meter, name, additional) => JSON.stringify([meter, chartSelections(name, additional)]);

const fetchChartSeries = async (meter, from, to, name, additional, signal) => {
    const results = await Promise.all(chartSelections(name, additional).map(async (selection) => {
        try {
            const response = await api.get(
                `chart/${meter}/${selection.selected}/${encodeURIComponent(from)}/${encodeURIComponent(to)}`,
                { withCredentials: true, signal },
            );
            if (!response.data.success) return { ...selection, dataPoints: [], failed: true };
            const { data, unit, step } = response.data.payload;
            const dataPoints = data.map((track) => [...track]
                .sort((a, b) => new Date(a.date + 'Z') - new Date(b.date + 'Z'))
                .map((point) => ({ x: new Date(point.date + 'Z'), y: point.value })));
            return { ...selection, dataPoints, unit, step };
        } catch (error) {
            if (signal.aborted) throw error;
            return { ...selection, dataPoints: [], failed: true };
        }
    }));
    return {
        series: [0, 1, 2].map((id) => {
            const result = results.find((item) => item.id === id);
            return { id, selected: result?.selected || '', dataPoints: result?.dataPoints || [] };
        }),
        unit: results[0].unit || '',
        step: results[0].step,
        noData: !results[0].dataPoints.some((track) => track.length > 0),
        failed: results.some((result) => result.failed),
    };
};

const downsampleViewportData = (series, minX, maxX, maxPoints = 14400, isReset = false) => {
    return series.map(serie => ({
        ...serie,
        dataPoints: serie.dataPoints.map(points => {
            const filtered = isReset ? points : points.filter(p => p.x >= minX && p.x <= maxX);

            const rawStep = Math.floor(filtered.length / maxPoints);
            const step = Math.max(1, Math.floor(rawStep / 2));

            if (step <= 1) return filtered;
            return filtered.filter((_, idx) => idx % step === 0);
        })
    }));
};


const Dashboard = () => {

    const { flow_meter_id, from_datetime, to_datetime } = useParams();

    const [noDataInitialUpper, setNoDataInitialUpper] = useState(false);
    const [noDataInitialLower, setNoDataInitialLower] = useState(false);
    
    const [selectedFromDate, setSelectedFromDate] = useState(format(from_datetime, 'yyyy-MM-dd'));
    const [selectedFromTime, setSelectedFromTime] = useState(format(from_datetime, 'HH:mm:ss'));
    const [selectedToDate, setSelectedToDate] = useState(format(to_datetime, 'yyyy-MM-dd'));
    const [selectedToTime, setSelectedToTime] = useState(format(to_datetime, 'HH:mm:ss'));

    const [selectedFromDateRange, setSelectedFromDateRange] = useState(format(from_datetime, 'yyyy-MM-dd'));
    const [selectedFromTimeRange, setSelectedFromTimeRange] = useState(format(from_datetime, 'HH:mm:ss'));
    const [selectedToDateRange, setSelectedToDateRange] = useState(format(to_datetime, 'yyyy-MM-dd'));
    const [selectedToTimeRange, setSelectedToTimeRange] = useState(format(to_datetime, 'HH:mm:ss'));
    
    const navigate = useNavigate();

    const [autoScaleY, setAutoScaleY] = useState(false);
    const [refreshVersion, setRefreshVersion] = useState(0);
    const [rangeRequestPending, setRangeRequestPending] = useState(false);
    const rangeRequestLock = useRef(false);
    const chartRequests = useRef(new Map());
    const [loadedUpperKey, setLoadedUpperKey] = useState('');
    const [loadedLowerKey, setLoadedLowerKey] = useState('');
    const scaledUpperKey = useRef(null);
    const scaledLowerKey = useRef(null);

    const chartRefUpper = useRef(null);
    const chartRefLower = useRef(null);
 
    const [vieportMinx, setViewportMinx] = useState(parseISO(from_datetime));
    const [vieportMaxx, setViewportMaxx] = useState(parseISO(to_datetime));

    const [dataToDownload, setDataToDownload] = useState();
    const [titles, setTitles] = useState();

    const charts = [chartRefUpper, chartRefLower];

    const [vieportMinYUpper, setVieportMinYUpper] = useState(null);
    const [vieportMaxYUpper, setVieportMaxYUpper] = useState(null);
    const [vieportMinYLower, setVieportMinYLower] = useState(null);
    const [vieportMaxYLower, setVieportMaxYLower] = useState(null);

    const [inputMinYUpper, setInputMinYUpper] = useState('')
    const [inputMaxYUpper, setInputMaxYUpper] = useState('')
    const [inputMinYLower, setInputMinYLower] = useState('')
    const [inputMaxYLower, setInputMaxYLower] = useState('')

    const chartOrder = [
        'Flow',
        'DT',
        'TTUp',
        'TTDn',
        'FlowVelocity',
        'AbsoluteSoundVelocity',
        'SoundVelocity',
        'AbsoluteRe',
        'Re',
        'SignalAmplitudeUp',
        'SignalAmplitudeDn',
        'RxGainUp',
        'RxGainDn',
        'SNRUp',
        'SNRDn',
        'CorrelationCoef',
        'ReferenceCoef',
        'PcbTemp',
        'ExternalInput',
        'BurstsAccepted',
        'Totalizer',
        'Temp',
        'StDev',
        'DigitalInput',

        'MeanFlow',
        'MeanDT',
        'Std',
        'StdDT',
        'DiffStd',
        'MeanSection',

        'DCVoltageElectrode',
        'ElectrodeDiffVoltage',
        'ImpedanceElectrode',
        'ImpedanceElectrodeUnfiltered',
        'RawVelocity',
        'CoilResistance',
        'CoilCurrentStatus',
        'CoilBoostTime',
        'SignalInputSpan',
        'SignalNoiseImmunity',
        'ElectricalConductivity',
    ];
    const titlesNoTrack = [
        'Flow',
        'Totalizer',
        'AbsoluteSoundVelocity',
        'AbsoluteRe',
        'PcbTemp',
        'ExternalTemp',
        'ExternalInput',
        'MeanFlow',
        'Std',
        'DiffStd',
        'MeanSection',
        'ElectrodeDiffVoltage',
        'RawVelocity',
        'CoilResistance',
        'CoilCurrentStatus',
        'CoilBoostTime',
        'SignalInputSpan',
        'SignalNoiseImmunity',
        'ElectricalConductivity',
    ]
    const titlesTrack = [
        'RxGainUp',
        'RxGainDn',
        'SNRUp',
        'SNRDn',
        'SoundVelocity',
        'FlowVelocity',
        'DT',
        'TTUp',
        'TTDn',
        'CorrelationCoef',
        'ReferenceCoef',
        'BurstsAccepted',
        'SignalAmplitudeUp',
        'SignalAmplitudeDn',
        'Temp',
        'Re',
        'StDev',
        'MeanDT',
        'StdDT',
        'DigitalInput',
        'DCVoltageElectrode',
        'ImpedanceElectrode',
        'ImpedanceElectrodeUnfiltered',
    ]
    const [location, setLocation] = useState({
        'place': '',
        'thoroughfare': '',
        'type_line': ''
    });

    const [currentDataPointsUpper, setCurrentDataPointsUpper] = useState([
        {id: 0, selected: '', dataPoints: []},
        {id: 1, selected: '', dataPoints: []},
        {id: 2, selected: '', dataPoints: []}
    ]);
    const [currentDataPointsLower, setCurrentDataPointsLower] = useState([
        {id: 0, selected: '', dataPoints: []},
        {id: 1, selected: '', dataPoints: []},
        {id: 2, selected: '', dataPoints: []}
    ]);

    const [fullDataPointsUpper, setFullDataPointsUpper] = useState([
        {id: 0, selected: '', dataPoints: []},
        {id: 1, selected: '', dataPoints: []},
        {id: 2, selected: '', dataPoints: []}
    ]);

    const [fullDataPointsLower, setFullDataPointsLower] = useState([
        {id: 0, selected: '', dataPoints: []},
        {id: 1, selected: '', dataPoints: []},
        {id: 2, selected: '', dataPoints: []}
    ]);

    const [upperChartName, setUpperChartName] = useState(chartOrder[0]);
    const [lowerChartName, setLowerChartName] = useState(chartOrder[1]);

    const [unitUpper, setUnitUpper] = useState('');
    const [unitLower, setUnitLower] = useState('');
    const [step, setStep] = useState('');

    const [noDataUpper, setNoDataUpper] = useState(false);
    const [noDataLower, setNoDataLower] = useState(false);

    const [loadingUpperChart, setLoadingUpperChart] = useState(false);
    const [loadingLowerChart, setLoadingLowerChart] = useState(false);

    const measurementFolderLink = process.env.REACT_APP_MEASUREMENT_FOLDER_LINK;

    const [upperAdditionalSelects, setUpperAdditionalSelects] = useState([]);
    const [lowerAdditionalSelects, setLowerAdditionalSelects] = useState([]);


    const [averageValuesTableSet, setAverageValuesTableSet] = useState(0);
    const [loadingAverageCalculation, setLoadingAverageCalculation] = useState(false);
    const [isInitialCheckClicked, setIsInitialCheckClicked] = useState(false);
    const [averageValues, setAverageValues] = useState(
        {
            'interval': {
                'value': null,
                'unit': null
            },
            'vol_of_fluid': {
                'value': null,
                'unit': null
            },
            'flow': {
                'value': null,
                'unit': null
            },
            'dt1': {
                'value': null,
                'unit': null
            },
            'dt2': {
                'value': null,
                'unit': null
            },
            'dt3': {
                'value': null,
                'unit': null
            },
            'diffdt12': {
                'value': null,
                'unit': null
            },
            'soundvelocity1': {
                'value': null,
                'unit': null
            },
            'soundvelocity2': {
                'value': null,
                'unit': null
            },
            'soundvelocity3': {
                'value': null,
                'unit': null
            },
            're1': {
                'value': null,
                'unit': null
            },
            're2': {
                'value': null,
                'unit': null
            },
            're3': {
                'value': null,
                'unit': null
            },
            'temp1': {
                'value': null,
                'unit': null
            },
            'temp2': {
                'value': null,
                'unit': null
            },
            'temp3': {
                'value': null,
                'unit': null
            },
            'temp11': {
                'value': null,
                'unit': null
            },
            'temp22': {
                'value': null,
                'unit': null
            },
            'temp33': {
                'value': null,
                'unit': null
            },
            'density1': {
                'value': null,
                'unit': null
            },
            'density2': {
                'value': null,
                'unit': null
            },
            'density3': {
                'value': null,
                'unit': null
            },
            'density11': {
                'value': null,
                'unit': null
            },
            'density22': {
                'value': null,
                'unit': null
            },
            'density33': {
                'value': null,
                'unit': null
            },
            'viscosity1': {
                'value': null,
                'unit': null
            },
            'viscosity2': {
                'value': null,
                'unit': null
            },
            'viscosity3': {
                'value': null,
                'unit': null
            },
            'viscosity11': {
                'value': null,
                'unit': null
            },
            'viscosity22': {
                'value': null,
                'unit': null
            },
            'viscosity33': {
                'value': null,
                'unit': null
            },
        });

    const handleCheckAverageValues = () => {
        if (!isInitialCheckClicked) {
            setIsInitialCheckClicked(true);
        }
        setAverageValuesTableSet(prev => prev + 1);
    }

    const addAdditionalSelect = (additionalSelects, setAdditionalSelects) => {
        if (additionalSelects.length < 2) {
            setAdditionalSelects(
                prev => [
                    ...prev,
                    {id: prev.length+1, selected: ''}
                ]
            )
        } else {
            return null;
        }
    };

    const getLatestAddiditonalSelectId = (selects) => {
        if (selects.length === 0) return null;
        return selects[selects.length - 1].id
    }

    const removeAdditionalSelect = (additionalSelects, setAdditionalSelects, setCurrentDataPoints, fullDataPoints) => {
        const id = getLatestAddiditonalSelectId(additionalSelects);
        setAdditionalSelects(prev => prev.filter(s => s.id !== id))
        setCurrentDataPoints(prev =>
            prev.map(i =>
                i.id === id
                    ? { id: i.id, selected: '', dataPoints: [] }
                    : i
            )
        );
        if (fullDataPoints === "upper")
            setFullDataPointsUpper(prev =>
                prev.map(i =>
                    i.id === id
                        ? { id: i.id, selected: '', dataPoints: [] }
                        : i
                )
            );
        if (fullDataPoints === "lower")
            setFullDataPointsLower(prev =>
                prev.map(i =>
                    i.id === id
                        ? { id: i.id, selected: '', dataPoints: [] }
                        : i
                )
            );
    }

    const handleAdditionalSelectChange = (setAdditionalSelects, id, selectedChartname) => {
        setAdditionalSelects(
            prev => prev.map(
                d => d.id === id ? {...d, selected: selectedChartname} : d
            )
        )
    }

    const syncHandler = (e) => {
        if (e.trigger === "reset") {
            // Przywracamy pełny zakres osi X
            const fromDate = parseISO(from_datetime);
            const toDate = parseISO(to_datetime);

            for (let i = 0; i < charts.length; i++) {
                charts[i].current?.axisX[0]?.set("viewportMinimum", fromDate);
                charts[i].current?.axisX[0]?.set("viewportMaximum", toDate);
            }

            setViewportMinx(fromDate);
            setViewportMaxx(toDate);

            setSelectedFromDateRange(format(fromDate, 'yyyy-MM-dd'));
            setSelectedFromTimeRange(format(fromDate, 'HH:mm:ss'));
            setSelectedToDateRange(format(toDate, 'yyyy-MM-dd'));
            setSelectedToTimeRange(format(toDate, 'HH:mm:ss'));

            // Reset danych – wszystkie punkty
            const newDataUpper = downsampleViewportData(fullDataPointsUpper, fromDate, toDate, 14400, true);
            const newDataLower = downsampleViewportData(fullDataPointsLower, fromDate, toDate, 14400, true);

            setCurrentDataPointsUpper(newDataUpper);
            setCurrentDataPointsLower(newDataLower);

            if (e.chart.options.id === 1) {
                fitUpperY(fullDataPointsUpper.map((series) => ({ points: series.dataPoints })));
            } else {
                fitLowerY(fullDataPointsLower.map((series) => ({ points: series.dataPoints })));
            }

        } else {
            // Normalne przesuwanie / zoom
            const minx = parseInt(e.chart.axisX[0].viewportMinimum);
            const maxx = parseInt(e.chart.axisX[0].viewportMaximum);
            const miny = e.chart.axisY[0].viewportMinimum;
            const maxy = e.chart.axisY[0].viewportMaximum;

            if (e.chart.options.id === 1) {
                setVieportMinYUpper(miny);   
                setVieportMaxYUpper(maxy);   
                setInputMinYUpper(miny);   
                setInputMaxYUpper(maxy);
                charts[1].current?.axisX[0]?.set("viewportMinimum", minx);
                charts[1].current?.axisX[0]?.set("viewportMaximum", maxx);
            } else if (e.chart.options.id === 2) {
                setVieportMinYLower(miny);   
                setVieportMaxYLower(maxy);   
                setInputMinYLower(miny);
                setInputMaxYLower(maxy);
                charts[0].current?.axisX[0]?.set("viewportMinimum", minx);
                charts[0].current?.axisX[0]?.set("viewportMaximum", maxx);
            }

            setViewportMinx(minx);
            setViewportMaxx(maxx);

            const from = new Date(minx);
            const to = new Date(maxx);
            setSelectedFromDateRange(format(from, 'yyyy-MM-dd'));
            setSelectedFromTimeRange(format(from, 'HH:mm:ss'));
            setSelectedToDateRange(format(to, 'yyyy-MM-dd'));
            setSelectedToTimeRange(format(to, 'HH:mm:ss'));

            // Filtrujemy i downsamplujemy dane tylko dla aktualnego viewport
            const newDataUpper = downsampleViewportData(fullDataPointsUpper, minx, maxx);
            const newDataLower = downsampleViewportData(fullDataPointsLower, minx, maxx);

            setCurrentDataPointsUpper(newDataUpper);
            setCurrentDataPointsLower(newDataLower);
        }
    }

    const fitUpperY = useCallback((series) => {
        const range = getChartYRange(series);
        if (!range) return false;
        setVieportMinYUpper(range.minimum);
        setVieportMaxYUpper(range.maximum);
        setInputMinYUpper(range.minimum);
        setInputMaxYUpper(range.maximum);
        return true;
    }, []);

    const fitLowerY = useCallback((series) => {
        const range = getChartYRange(series);
        if (!range) return false;
        setVieportMinYLower(range.minimum);
        setVieportMaxYLower(range.maximum);
        setInputMinYLower(range.minimum);
        setInputMaxYLower(range.maximum);
        return true;
    }, []);

    const resetUpperYaxes = () => fitUpperY(dataPointsUpper);
    const resetLowerYaxes = () => fitLowerY(dataPointsLower);

    const hasAnyPoints = (datapoints) => {
        if (!Array.isArray(datapoints)) return false;

        return datapoints.some(
            serie => Array.isArray(serie.points) && serie.points.some((track) => Array.isArray(track) && track.length > 0)
        );
    };

    useEffect(() => {
        if (isInitialCheckClicked) {
            const getAverageValues = async () => {
                setLoadingAverageCalculation(true);
                const dateTimeFrom = `${selectedFromDateRange}T${selectedFromTimeRange}`;
                const dateTimeTo = `${selectedToDateRange}T${selectedToTimeRange}`;
                try {   
                        const response = await api.get(
                            `average-values/${flow_meter_id}/${encodeURIComponent(dateTimeFrom)}/${encodeURIComponent(dateTimeTo)}`,
                            {withCredentials: true}
                        );
                        if (response.data.success) {
                            setAverageValues(response.data.payload);
                            toast.success('Successfully calculated average values!');
                        } else {
                            toast.error('Not calculated average values!')
                            setLoadingAverageCalculation(false);
                        }
                } catch (err) {
                        console.log(err.response);
                }
                setLoadingAverageCalculation(false);
            }
            getAverageValues();
        }
    }, [averageValuesTableSet])

    const getAvailableNamesOfChartToDownload = () => {
        const loc_titles = [];
        for (const chart_name of chartOrder) {
                loc_titles.push(chart_name);
        }
        return loc_titles
    }

    const handleDownloadCsvButton = () => {
        const dateTimeFromParsed = parseISO(`${selectedFromDateRange}T${selectedFromTimeRange}`);
        const dateTimeToParsed = parseISO(`${selectedToDateRange}T${selectedToTimeRange}`);
        const difference = Math.abs(differenceInHours(dateTimeFromParsed, dateTimeToParsed));
        if (difference > 23) {
            toast.error('Cannot get data more than 24 hours!');
            return
        }
        const loc_titles = getAvailableNamesOfChartToDownload();
        setTitles(loc_titles);
    }

    const handleConfirmRangeChange = () => {
        if (rangeRequestLock.current || chartRequests.current.size > 0) return;
        const areDatesAndTimesSet = selectedFromDate &&
                                    selectedFromTime &&
                                    selectedToDate &&
                                    selectedToTime

        if (!areDatesAndTimesSet) {
            toast.error('Error! Insert all dates and times!');
            return
        }

        const dateTimeFromBeforeParsing = `${selectedFromDate}T${selectedFromTime}`
        const dateTimeToBeforeParsing = `${selectedToDate}T${selectedToTime}`

        const dateTimeFromParsed = parseISO(dateTimeFromBeforeParsing)
        const dateTimeToParsed = parseISO(dateTimeToBeforeParsing)

        if (!isBefore(dateTimeFromParsed, dateTimeToParsed)) {
            toast.error('Error! First datetime is not earlier!')
            return
        }

        rangeRequestLock.current = true;
        setRangeRequestPending(true);
        if (dateTimeFromBeforeParsing === from_datetime && dateTimeToBeforeParsing === to_datetime) {
            setRefreshVersion((version) => version + 1);
        } else {
            navigate(`/dashboard/dashboard/${flow_meter_id}/${dateTimeFromBeforeParsing}/${dateTimeToBeforeParsing}`);
        }
    }

    const handleResetRangeChange = () => {
        setSelectedFromDate(format(from_datetime, 'yyyy-MM-dd'));
        setSelectedFromTime(format(from_datetime, 'HH:mm:ss'));
        setSelectedToDate(format(to_datetime, 'yyyy-MM-dd'));
        setSelectedToTime(format(to_datetime, 'HH:mm:ss'));
    }

    const handleSignals = () => {
        toast.error('This feature will be available in the future...')
    }

    const sortData = (data) => {
        return [...data].sort((a,b) => new Date(a.date) - new Date(b.date));
    }

    useEffect(() => {
        const getAllDataForButtonDownload = async () => {
            const task_id = uuidv4();
            const toastId = toast('');
            toast.loading("Getting data for csv...", {id: toastId});
            const allData = [];
            const dateTimeFromRange = `${selectedFromDateRange}T${selectedFromTimeRange}`
            const dateTimeToRange = `${selectedToDateRange}T${selectedToTimeRange}`

            const socket = createSocket(task_id);

            socket.onopen = () => {
                console.log('Connection opened!');
            };

            socket.onmessage = (event) => {
                const data = json_parser(event.data);                    
                if (data.message !== '') {
                    toast.loading(
                        `State: ${data.current_state}/${data.all_states} ${data.message}`,
                        {
                            id: toastId,
                            style: {
                                width: '40rem'
                            }
                        }
                    );
                } else {
                    toast.error(`State: ${data.current_state}/${data.all_states} ${data.error}`,
                        {
                            id: toastId,
                            style: {
                                width: '40rem'
                            },
                            duration: 10000
                        }
                    );
                }

                if (data.message !== '') {
                    if (data.current_state === 5) {
                        toast.success(
                            `State: ${data.current_state}/${data.all_states} ${data.message}`,
                            {
                                id: toastId,
                                style: {
                                    width: '35rem'
                                }
                            }
                        );
                    }
                }

                if (data.error !== '') {
                    if (data.current_state === 4.5) {
                        toast.error(`State: 4/${data.all_states} ${data.error}`,
                            {
                                id: toastId,
                                style: {
                                    width: '40rem'
                                },
                                duration: 5000
                            }
                        );
                    }
                };
            }
            socket.onclose = () => {
                console.log('Connection closed!');
            };

            try {   
                const response = await api.put(
                    `download/data_for_csv`,
                    {
                        flow_meter_id: flow_meter_id,
                        datetime_from: dateTimeFromRange,
                        datetime_to: dateTimeToRange,
                        available_charts: titles,
                        websocket_id: task_id
                    },
                    {
                        withCredentials: true,
                        responseType: 'blob'
                    }
                );
                if (response.status !== 200) {
                    toast.error(
                        `State: 3/4 CSV downloading error!`,
                        {
                            id: toastId,
                            style: {
                                width: '35rem'
                            },
                            duration: 5000
                        }
                    );
                } else {
                    setDataToDownload(response);
                    toast.success(
                        `State: 4/4 Data downloaded successfully`,
                        {
                            id: toastId,
                            style: {
                                width: '35rem'
                            },
                            duration: 5000
                        }
                    );
                }                
                socket.close()
            } catch (err) {
                console.log(err.response);
            }
        }

        if (titles) {
            getAllDataForButtonDownload();
        }
        
    }, [titles]);


    useEffect(() => {
        if (dataToDownload && titles) {
            handleDownloadCsv(dataToDownload);
            setDataToDownload();
            setTitles();
        }

    }, [dataToDownload]);

    const handleSubmit = (e) => {
        e.preventDefault()
        const dateTimeFromBeforeParsing = `${selectedFromDateRange}T${selectedFromTimeRange}`
        const dateTimeToBeforeParsing = `${selectedToDateRange}T${selectedToTimeRange}`
        const dateTimeFromParsed = parseISO(dateTimeFromBeforeParsing)
        const dateTimeToParsed = parseISO(dateTimeToBeforeParsing)
        if (!isBefore(dateTimeFromParsed, dateTimeToParsed)) {
            toast.error('Error! `From` datetime is not earlier!')
            return
        }

        const validYRange = (minimum, maximum) => minimum !== '' && maximum !== ''
            && Number.isFinite(Number(minimum)) && Number.isFinite(Number(maximum))
            && Number(minimum) < Number(maximum);
        if (hasAnyPoints(dataPointsLower) && !validYRange(inputMinYLower, inputMaxYLower)) {
            toast.error('Set a valid lower Y range: MIN must be smaller than MAX.');
            return;
        }
        if (hasAnyPoints(dataPointsUpper) && !validYRange(inputMinYUpper, inputMaxYUpper)) {
            toast.error('Set a valid upper Y range: MIN must be smaller than MAX.');
            return;
        }

        const newDataUpper = downsampleViewportData(
            fullDataPointsUpper,
            dateTimeFromParsed,
            dateTimeToParsed
        );

        const newDataLower = downsampleViewportData(
            fullDataPointsLower,
            dateTimeFromParsed,
            dateTimeToParsed
        );

        setCurrentDataPointsUpper(newDataUpper);
        setCurrentDataPointsLower(newDataLower);

        for (var i = 0; i < charts.length; i++) {
                charts[i].current?.axisX[0]?.set("viewportMinimum", dateTimeFromParsed);
                charts[i].current?.axisX[0]?.set("viewportMaximum", dateTimeToParsed);
            }

        if (hasAnyPoints(dataPointsLower)) {
            setVieportMaxYLower(Number(inputMaxYLower));
            setVieportMinYLower(Number(inputMinYLower));
        }
        if (hasAnyPoints(dataPointsUpper)) {
            setVieportMaxYUpper(Number(inputMaxYUpper));
            setVieportMinYUpper(Number(inputMinYUpper));
        }
        setViewportMinx(dateTimeFromParsed);
        setViewportMaxx(dateTimeToParsed);
    }

    useEffect(() => {
        const getFlowmeasurementDescriptionData = async() => {
            try {
                const response = await api.get(`${flow_meter_id}`, {withCredentials: true})
                if (response.data.success) {
                    setLocation(response.data.payload.location);
                } else {
                    toast.error('Not got description data from database!')   
                }
            } catch (err) {
                console.log(err.response);
            }
        }
        getFlowmeasurementDescriptionData();
    }, [flow_meter_id]);
    
    useEffect(() => {
        setSelectedFromDate(format(from_datetime, 'yyyy-MM-dd'));
        setSelectedFromTime(format(from_datetime, 'HH:mm:ss'));
        setSelectedToDate(format(to_datetime, 'yyyy-MM-dd'));
        setSelectedToTime(format(to_datetime, 'HH:mm:ss'));
        setSelectedFromDateRange(format(from_datetime, 'yyyy-MM-dd'));
        setSelectedFromTimeRange(format(from_datetime, 'HH:mm:ss'));
        setSelectedToDateRange(format(to_datetime, 'yyyy-MM-dd'));
        setSelectedToTimeRange(format(to_datetime, 'HH:mm:ss'));
        setViewportMinx(parseISO(from_datetime));
        setViewportMaxx(parseISO(to_datetime));
    }, [flow_meter_id, from_datetime, to_datetime]);

    const upperSelectionKey = chartSelectionKey(flow_meter_id, upperChartName, upperAdditionalSelects);
    const lowerSelectionKey = chartSelectionKey(flow_meter_id, lowerChartName, lowerAdditionalSelects);

    const finishChartRequest = useCallback((position, controller) => {
        if (chartRequests.current.get(position) !== controller) return;
        chartRequests.current.delete(position);
        if (chartRequests.current.size === 0) {
            rangeRequestLock.current = false;
            setRangeRequestPending(false);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        chartRequests.current.set('upper', controller);
        setLoadingUpperChart(true);
        fetchChartSeries(flow_meter_id, from_datetime, to_datetime, upperChartName, upperAdditionalSelects, controller.signal)
            .then((result) => {
                if (controller.signal.aborted) return;
                setFullDataPointsUpper(result.series);
                setCurrentDataPointsUpper(result.series);
                setLoadedUpperKey(upperSelectionKey);
                setUnitUpper(result.unit);
                setStep(result.step);
                setNoDataUpper(result.noData);
                setNoDataInitialUpper(true);
                if (result.failed) toast.error('Could not load all data for the upper chart.');
            }).catch(() => {}).finally(() => {
                if (controller.signal.aborted) return;
                setLoadingUpperChart(false);
                finishChartRequest('upper', controller);
            });
        return () => controller.abort();
    }, [flow_meter_id, from_datetime, to_datetime, upperChartName, upperAdditionalSelects, upperSelectionKey, refreshVersion, finishChartRequest]);

    useEffect(() => {
        const controller = new AbortController();
        chartRequests.current.set('lower', controller);
        setLoadingLowerChart(true);
        fetchChartSeries(flow_meter_id, from_datetime, to_datetime, lowerChartName, lowerAdditionalSelects, controller.signal)
            .then((result) => {
                if (controller.signal.aborted) return;
                setFullDataPointsLower(result.series);
                setCurrentDataPointsLower(result.series);
                setLoadedLowerKey(lowerSelectionKey);
                setUnitLower(result.unit);
                setStep(result.step);
                setNoDataLower(result.noData);
                setNoDataInitialLower(true);
                if (result.failed) toast.error('Could not load all data for the lower chart.');
            }).catch(() => {}).finally(() => {
                if (controller.signal.aborted) return;
                setLoadingLowerChart(false);
                finishChartRequest('lower', controller);
            });
        return () => controller.abort();
    }, [flow_meter_id, from_datetime, to_datetime, lowerChartName, lowerAdditionalSelects, lowerSelectionKey, refreshVersion, finishChartRequest]);

    const dataPointsUpper = useMemo(() => currentDataPointsUpper.map((series) => ({
        title: series.selected,
        points: downsampleViewportData([series], vieportMinx, vieportMaxx, 500)[0].dataPoints,
    })), [currentDataPointsUpper, vieportMinx, vieportMaxx]);

    const dataPointsLower = useMemo(() => currentDataPointsLower.map((series) => ({
        title: series.selected,
        points: downsampleViewportData([series], vieportMinx, vieportMaxx, 500)[0].dataPoints,
    })), [currentDataPointsLower, vieportMinx, vieportMaxx]);

    useEffect(() => {
        if (loadedUpperKey !== upperSelectionKey) return;
        if ((autoScaleY || scaledUpperKey.current !== upperSelectionKey) && fitUpperY(dataPointsUpper)) {
            scaledUpperKey.current = upperSelectionKey;
        }
    }, [autoScaleY, dataPointsUpper, loadedUpperKey, upperSelectionKey, fitUpperY]);

    useEffect(() => {
        if (loadedLowerKey !== lowerSelectionKey) return;
        if ((autoScaleY || scaledLowerKey.current !== lowerSelectionKey) && fitLowerY(dataPointsLower)) {
            scaledLowerKey.current = lowerSelectionKey;
        }
    }, [autoScaleY, dataPointsLower, loadedLowerKey, lowerSelectionKey, fitLowerY]);

    const handleOpenMeasurementFolder = () => {
        window.open(measurementFolderLink, '_blank');
    };

    const handleOpenMeasurementNotes = () => {
        navigate(`../notes/${flow_meter_id}/${from_datetime}/${to_datetime}`);
    };

    return (
        <div className="dashboard-container">
            <h2 className="dashboard-title">Dashboard</h2>
            
            <DashboardMainTable
                selectedFromDate={selectedFromDate}
                setSelectedFromDate={setSelectedFromDate}
                selectedFromTime={selectedFromTime}
                setSelectedFromTime={setSelectedFromTime}
                selectedToDate={selectedToDate}
                setSelectedToDate={setSelectedToDate}
                selectedToTime={selectedToTime}
                setSelectedToTime={setSelectedToTime}
                handleConfirmRangeChange={handleConfirmRangeChange}
                loadingCharts={rangeRequestPending || loadingUpperChart || loadingLowerChart}
                handleResetRangeChange={handleResetRangeChange}
                handleSignals={handleSignals}
                location={location}
                step={step}
                addAdditionalSelect={addAdditionalSelect}
                upperAdditionalSelects={upperAdditionalSelects}
                setUpperAdditionalSelects={setUpperAdditionalSelects}
                removeAdditionalSelect={removeAdditionalSelect}
                setCurrentDataPointsUpper={setCurrentDataPointsUpper}
                chartOrder={chartOrder}
                upperChartName={upperChartName}
                setUpperChartName={setUpperChartName}
                handleAdditionalSelectChange={handleAdditionalSelectChange}
                lowerAdditionalSelects={lowerAdditionalSelects}
                setLowerAdditionalSelects={setLowerAdditionalSelects}
                setCurrentDataPointsLower={setCurrentDataPointsLower}
                lowerChartName={lowerChartName}
                setLowerChartName={setLowerChartName}
                handleOpenMeasurementFolder={handleOpenMeasurementFolder}
                handleOpenMeasurementNotes={handleOpenMeasurementNotes}
                handleCheckAverageValues={handleCheckAverageValues}
                isInitialCheckClicked={isInitialCheckClicked}
                averageValues={averageValues}
                loadingAverageCalculation={loadingAverageCalculation}
            />
            
            { hasAnyPoints(dataPointsUpper) && vieportMinx?
                <div className="dashboard-slider-wrapper">
                    <LineChart
                        id={1}
                        syncHandler={syncHandler}
                        chartRef={chartRefUpper}
                        chart_title={upperChartName}
                        dataPoints={dataPointsUpper}
                        unit={`[${unitUpper}]`}
                        min={vieportMinx}
                        max={vieportMaxx}
                        initmin={parseISO(from_datetime)}
                        initmax={parseISO(to_datetime)}
                        miny={vieportMinYUpper}
                        maxy={vieportMaxYUpper}
                        step={step}
                    />
                {noDataUpper && !loadingUpperChart ? <div className="no-data-div"> No data available for {upperChartName} </div>: null}
                {loadingUpperChart ? <div className="no-data-div" role="status" aria-label="Loading upper chart"> <Spinner decorative /> </div>: null}
                </div>
            : loadingUpperChart || !noDataInitialUpper ? <Spinner label="Loading upper chart" />: <p style={{color: 'white', fontSize: '1.5rem'}}>No available data for {upperChartName}</p>}
            
            <DashboardRange 
                handleSubmit={handleSubmit}
                autoScaleY={autoScaleY}
                setAutoScaleY={setAutoScaleY}
                resetUpperYaxes={resetUpperYaxes}
                inputMaxYUpper={inputMaxYUpper}
                setInputMaxYUpper={(value) => { setAutoScaleY(false); setInputMaxYUpper(value); }}
                inputMinYUpper={inputMinYUpper}
                setInputMinYUpper={(value) => { setAutoScaleY(false); setInputMinYUpper(value); }}
                resetLowerYaxes={resetLowerYaxes}
                inputMaxYLower={inputMaxYLower}
                setInputMaxYLower={(value) => { setAutoScaleY(false); setInputMaxYLower(value); }}
                inputMinYLower={inputMinYLower}
                setInputMinYLower={(value) => { setAutoScaleY(false); setInputMinYLower(value); }}
                selectedFromDateRange={selectedFromDateRange}
                setSelectedFromDateRange={setSelectedFromDateRange}
                selectedFromTimeRange={selectedFromTimeRange}
                setSelectedFromTimeRange={setSelectedFromTimeRange}
                selectedToDateRange={selectedToDateRange}
                setSelectedToDateRange={setSelectedToDateRange}
                selectedToTimeRange={selectedToTimeRange}
                setSelectedToTimeRange={setSelectedToTimeRange}
                handleDownloadCsvButton={handleDownloadCsvButton}
            />

            { hasAnyPoints(dataPointsLower) && vieportMinx?
                <div className="dashboard-slider-wrapper">
                    <LineChart
                        id={2}
                        syncHandler={syncHandler}
                        chartRef={chartRefLower}
                        chart_title={lowerChartName}
                        dataPoints={dataPointsLower}
                        unit={`[${unitLower}]`}
                        min={vieportMinx}
                        max={vieportMaxx}
                        initmin={parseISO(from_datetime)}
                        initmax={parseISO(to_datetime)}
                        miny={vieportMinYLower}
                        maxy={vieportMaxYLower}
                        step={step}
                    />
                    {noDataLower && !loadingLowerChart ? <div className="no-data-div"> No data available for {lowerChartName} </div>: null}
                    {loadingLowerChart ? <div className="no-data-div" role="status" aria-label="Loading lower chart"> <Spinner decorative /> </div>: null}
                </div>
            : loadingLowerChart || !noDataInitialLower ? <Spinner label="Loading lower chart" />: <p style={{color: 'white', fontSize: '1.5rem'}}>No available data for {lowerChartName}</p>}
        </div>
    )}

export default Dashboard;
